import React, { useState, useEffect, useRef } from 'react'
import { DataGrid, Column, Export, GroupPanel, Grouping, FilterRow, Pager, Paging, StateStoring, HeaderFilter, Selection, Sorting } from 'devextreme-react/data-grid';
import moment, { Moment } from 'moment'
import Switch from 'devextreme-react/switch';
import {  FormattedMessage, IntlShape, useIntl } from 'react-intl';
import deMessages from "devextreme/localization/messages/de.json";
import frMessages from "devextreme/localization/messages/fr.json";
import esMessages from "devextreme/localization/messages/es.json";
import ptMessages from "devextreme/localization/messages/pt.json";
import { locale, loadMessages } from "devextreme/localization";
import { Chip, Button } from '@mui/material';
import { ColumnCellTemplateData } from 'devextreme/ui/data_grid'
import Modal from 'react-bootstrap/Modal'
import AlarmSetModal from './NewAlarmsModal'
import {AlarmData, Segments, Contact } from '../Alarm Functions/AlarmOverviewFunctions'
import AlarmContactModal from '../Alarms/AlarmContactModal'
import RecipientsModal from './AlarmReceipientsModal';
import AlarmRecipientsGroupModal from './AlarmReceipientGroupModal'
import { CreateUrl, CallGetAPI, CallPutAPI, CallPostAPI, CallDeleteAPI } from 'Utils/ApiHelper.js';
import { adjustTime, adjustTimeToUtc } from '../../Utils/AdjustTime' 
import { NotificationContainer, NotificationManager } from 'react-notifications';
import Loader from '../../Utils/Loader/NewLoader2025'
import AlarmHelpModal from '../Alarms/AlarmHelpModal'
import AlarmHistoryModal from '../Alarms/AlarmHistoryModal'
import Tooltip from '@mui/material/Tooltip';

type AlarmDataDisplay = Omit<AlarmData,
    'channel' |
    'value' |
    'unit' |
    'sendTimeframeStart' |
    'sendTimeframeEnd' |
    'countType' |
    'maxAlarms' |
    'count' |
    'countTrigger' |
    'clearValue' |
    'consumptionTimeframe' |
    'consumptionTimeframeUnit'
    >

interface Props {
    LCLoggerId: number
    model: string
    serial: number

}

interface APIAlarmData {
    alarmClass: number;
    contacts: any[];
    alarmMask: number;
    alarmMessage: string;
    lastTriggered: string;
    clrCount: number;
    clrInput: number;
    clrOperator: number;
    clrThreshold: number;
    consumptionTimeframe: string | null;
    consumptionUnit: string | null;
    createdBy: string | null;
    createdUTC: string | null;
    deletedBy: string | null;
    deletedUTC: string | null;
    fkLCLogger: unknown;
    fkLCLoggerId: number;
    id: number;
    maxAlarms: number;
    on: boolean;
    sendDateRangeEnd: string | null;
    sendDateRangeStart: string | null;
    sendTimeframeEndUTC: string | null;
    sendTimeframeStartUTC: string | null;
    setCount: number;
    setInput: number;
    setOperator: number;
    setThreshold: number;
    unit: string;
    status: string;
}



const Alarms: React.FC<Props> = ({LCLoggerId, model, serial }) => {

    ///Hooks
    const intl: IntlShape = useIntl()
    locale(sessionStorage.getItem('language') ? sessionStorage.getItem('language'): 'en-GB');
    loadMessages(frMessages);
    loadMessages(deMessages);
    loadMessages(esMessages);
    loadMessages(ptMessages);


    const ALARM_TYPE_BY_SET_INPUT: Record<number, string> = {
        1: "flow A",
        9: "flow B",
        11: "pressureDiff C",
        12: "pressureDiff D",
        21: "consumption A",
        22: "consumption B",
        30: "signal",
        31: "battery",
        40: "temperature",
        41: "mnf A",
        42: "mnf B",
        43: "noData",
        44: "digital A",
        45: "digital B",
        3: "latest Pressure C",
        7: "latest Pressure D",
        46: "analogDropOut C",
        47: "analogDropOut D",
        48: "latest Analogue C",
        49: "latest Analogue D"
    };

    const ALARM_CONDITION_KEYS: Record<number, string> = {
        0: 'Off',
        1: '=',
        2: '!=',
        3: '<',
        4: '>',
        5: '<=',
        6: '>='
    }

    const newAlarm = {
        id: null,
        on: true,
        created: null,
        createdBy: sessionStorage.getItem('userName') ? sessionStorage.getItem('userName') : '',
        lastTriggered: null,
        status: null,
        type: '',
        channel: '',
        condition: '',
        value: 0,
        clearCondition: '',
        clearValue: 0,
        consumptionTimeframe: 0,
        consumptionTimeframeUnit: '',
        unit: '',
        sendTimeframeStart: null,
        sendTimeframeEnd: null,
        sendDateRangeStart: null,
        sendDateRangeEnd: null,
        maxAlarms: 1,
        count: 0,
        clearCount: 0,
        message: '',
        contacts: [],
        alarmMask: 0,
        alarmClass: 8
    }

    const newSegments = {
        page1: { num: 0, text: '' },
        page2: { num: 0, text: ['', '']},
        page3: { num: 0, text: ['', '']},
        page4: { num: 0, text: ['','','','',''] },
    }

    const textForScreen = {
        flow: intl.formatMessage({ id: 'Flow' }),
        consumption: intl.formatMessage({ id: 'Consumption' }),
        pressure: intl.formatMessage({ id: 'Latest Pressure' }),
        pressureDiff: intl.formatMessage({ id: 'Pressure Difference' }),
        signal: intl.formatMessage({ id: 'Signal' }),
        battery: intl.formatMessage({ id: 'Battery' }),
        temperature: intl.formatMessage({ id: 'Temperature' }),
        mnf: intl.formatMessage({ id: 'Minimum Night Flow' }),
        analog: intl.formatMessage({ id: '4-20mA Reading' }),
        digital: intl.formatMessage({ id: 'Digital Triggered' }),
        analogDropOut: intl.formatMessage({ id: '4-20mA Sensor Error' }),
        noData: intl.formatMessage({ id: 'No Data in 24 hours' })
    }

    ///state
    const [loading, setLoading] = useState<boolean>(false)
    const [alarmData, setAlarmData] = useState<AlarmData[]>([])
    const [currentAlarms, setCurrentAlarms] = useState<AlarmDataDisplay[]>()
    const [showContacts, setShowContacts] = useState<boolean>(false)
    const [tempAlarm, setTempAlarm] = useState<AlarmData>()
    const [openCreateAlarmModal, setOpenCreateAlarmModal] = useState<boolean>(false)
    const [segments, setSegments] = useState<Segments>(newSegments)
    const [recipientsModal, setRecipientsModal] = useState<boolean>(false)
    const [recipientsGroupModal, setRecipientsGroupModal] = useState<boolean>(false)
    const [contactsForCompany, setContactsForCompany] = useState<Contact[]>([])
    const [editAlarm, setEditAlarm] = useState<boolean>(false)
    const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false)
    const [companyId, setCompanyId] = useState<number | null>(null)
    const [redirectId, setRedirectId] = useState<number | null>(null)

    const createSuccessNotification = (): void => {
        NotificationManager.success(intl.formatMessage({ id: 'Saved Changes' }), intl.formatMessage({ id: 'Success' }));

    };

    const createErrorNotification = (): void => {
        NotificationManager.error(intl.formatMessage({ id: 'Error Saving Changes' }), intl.formatMessage({ id: 'Check console for details' }), 5000, () => {
            alert('error');
        });
    };

    //// use Effects 
    useEffect(() => {
        reloadData()
        CallGetAPI(CreateUrl(`/api/aquaguard/getcompanyIdFromSerial?serial=${serial}`), {})
            .then(data => {
                setCompanyId(data)
            })
            .catch(err => console.error(err))
    }, [LCLoggerId, serial])

    const reloadData = ():void => {
        setLoading(true)
        CallGetAPI(CreateUrl(`/api/lclalarmconfigs/bylogger/${LCLoggerId - 500000}`), {})
            .then((data: APIAlarmData[]) => {
                const currentAlarmTableData: AlarmDataDisplay[] = []
                const alarmDataArr: AlarmData[] = []
                data.forEach((d: APIAlarmData) => {
                    console.log(d.status)
                    const condition = ALARM_CONDITION_KEYS[d.setOperator]
                    const clearCondition = ALARM_CONDITION_KEYS[d.clrOperator]
                    let type = ALARM_TYPE_BY_SET_INPUT[d.setInput]
                    let channel = ''
                    const channels = ['A', 'B', 'C', 'D']
                    if (d.setInput === 3) {
                        //channel C
                        type = 'pressure'
                        channel = 'C'
                    } else if (d.setInput === 7) {
                        // channel D
                        type = 'pressure'
                        channel = 'D'
                    } else if (d.setInput === 11) {
                        ///pressure C diff
                        type = 'pressureDiff'
                        channel = 'C'
                    } else if (d.setInput === 12) {
                        ///pressure D diff
                        type = 'pressureDiff'
                        channel = 'D'
                    } else if (d.setInput === 48) {
                        type = 'analog'
                        channel = 'C'
                    } else if (d.setInput === 49) {
                        type = 'analog'
                        channel = 'D'
                    }
                    else {
                        //others
                        if (channels.includes(type[type.length - 1])) {
                            channel = type.slice(type.length - 1, type.length)
                            type = type.slice(0, type.length - 2)

                        }
                    }
                    if (type === 'battery') {
                        d.setThreshold = d.setThreshold / 1000
                        d.clrThreshold = d.clrThreshold / 1000
                    }
                    let displayCondition = ''
                    let displayClrCondition = ''
                    if (condition === 'Off') {
                        displayCondition = intl.formatMessage({ id: 'N/A' })
                    } else {
                        displayCondition = `${condition} ${d.setThreshold} ${d.unit}`
                    }
                    if (clearCondition === 'Off') {
                        displayClrCondition = intl.formatMessage({ id: 'N/A' })
                    } else {
                        displayClrCondition = clearCondition ? `${clearCondition} ${d.clrThreshold} ${d.unit}` : `${d.consumptionTimeframe} ${d.consumptionUnit}`
                    } 
                    const almSummary: AlarmDataDisplay = {
                        id: d.id,
                        on: d.on,
                        created: d.createdUTC ? adjustTime(d.createdUTC) : '',
                        createdBy: d.createdBy,
                        lastTriggered: d.lastTriggered ? adjustTime(moment(d.lastTriggered).format('DD-MMM-YYYY HH:mm')) : intl.formatMessage({ id: 'No Alarm' }),
                        status: intl.formatMessage({ id: d.status }),
                        type: `${textForScreen[type]} ${intl.formatMessage({ id: 'on Channel' })} ${channel}`,
                        condition: displayCondition,
                        clearCondition: displayClrCondition,
                        sendTimeframe: d.sendTimeframeStartUTC && d.sendTimeframeEndUTC ?
                            `${moment(adjustTime(moment(d.sendTimeframeStartUTC, 'HH:mm:ss'))).format('HH:mm:ss')} - ${moment(adjustTime(moment(d.sendTimeframeEndUTC, 'HH:mm:ss'))).format('HH:mm:ss') }` : 
                            `${moment('00:00:00', 'HH:mm:ss')} - ${moment('23:59:59', 'HH:mm:ss')}`,
                        message: d.alarmMessage,
                        contacts: d.contacts.map(con => con.email)

                    }
                    const alarmData:AlarmData = {
                        id: d.id,
                        on: d.on,
                        created: adjustTime(d.createdUTC),
                        createdBy: d.createdBy,
                        lastTriggered: d.lastTriggered ? adjustTime(moment(d.lastTriggered).format('DD-MMM-YYYY HH:mm')) : intl.formatMessage({ id: 'No Alarm' }),
                        status: d.status,
                        type: type,
                        channel: channel,
                        condition: condition,
                        value: d.setThreshold,
                        clearCondition: clearCondition,
                        clearValue: d.clrThreshold,
                        consumptionTimeframe: d.consumptionTimeframe,
                        consumptionTimeframeUnit: d.consumptionUnit,
                        unit: d.unit,
                        sendTimeframeStart: d.sendTimeframeStartUTC ? moment(adjustTime(moment(d.sendTimeframeStartUTC, 'HH:mm:ss'))).format('HH:mm:ss') : null,
                        sendTimeframeEnd: d.sendTimeframeEndUTC ? moment(adjustTime(moment(d.sendTimeframeEndUTC, 'HH:mm:ss'))).format('HH:mm:ss') : null,
                        sendDateRangeStart: d.sendDateRangeStart ? moment(d.sendDateRangeStart, 'DDMM') : null,
                        sendDateRangeEnd: d.sendDateRangeEnd ? moment(d.sendDateRangeEnd, 'DDMM') : null,
                        maxAlarms: d.maxAlarms,
                        count: d.setCount,
                        clearCount: d.clrCount,
                        message: d.alarmMessage,
                        contacts: d.contacts.map(con => con.email),
                        alarmMask: d.alarmMask,
                        alarmClass: 8
                    }
                    currentAlarmTableData.push(almSummary)
                    alarmDataArr.push(alarmData)
                })
                setCurrentAlarms(currentAlarmTableData)
                setAlarmData(alarmDataArr)
                setLoading(false)
            })
            .catch(err => {
                console.error(err)
                setLoading(false)
            })

        
    }

    const handleAlarmChanges = (type: string, value: string | number | string[] | Moment | null): void => {
        const alm: AlarmData = { ...tempAlarm }
        if (type === 'type' && editAlarm) {
            setEditAlarm(false)
        }
        if (type === 'dateRange') {
            alm.sendDateRangeStart = moment(value[0], 'MM-DD')
            alm.sendDateRangeEnd = moment(value[1], 'MM-DD')


        } else if (type === 'sendTimeframeStart') {

            alm.sendTimeframeStart = moment(value, 'HH:mm')

        } else if (type === 'sendTimeframeEnd') {

            alm.sendTimeframeEnd = moment(value, 'HH:mm')

        } else if (type === 'value' && tempAlarm?.type === 'digital') {
            if (value === '1') {
                alm.value = 1
                alm.clearValue = 0
            } else {
                alm.value = 0
                alm.clearValue = 1
            }
        } else if (type === 'clearValue' && tempAlarm?.type === 'digital') {
            if (value === '0') {
                alm.value = 1
                alm.clearValue = 0
            } else {
                alm.value = 0
                alm.clearValue = 1
            }
        }
        else {
            alm[type] = value ? value : ''
        }
        
        handleSegmentsUpdate(type, value, alm, false)


        
        
    }

    useEffect(() => {
        if (tempAlarm?.type === 'digital' && !tempAlarm?.condition) {
            const currentSegs = { ...segments }
            currentSegs.page2.num = 2
            currentSegs.page2.text[1] = `= ${intl.formatMessage({ id: 'OFF' })} (0)`
            currentSegs.page2.text[0] = `= ${intl.formatMessage({ id: 'ON' })} (1)`
            setSegments(currentSegs)
        }
        
    }, [tempAlarm])

    



    useEffect(() => {
        if (companyId) {
            CallGetAPI(CreateUrl(`/api/aquaguard/Users?companyid=${companyId}`), {})
                .then(users => {

                    CallGetAPI(CreateUrl(`/api/aquaguard/ContactsForCompany?companyid=${companyId}`), {})
                        .then(data => {
                            const contactsTemp = []
                            data.forEach((c: Contact) => {
                                if (c.enabledSMS || c.enabledWhatsApp || c.enabledEmail) {
                                    const item = {
                                        id: c.id,
                                        fullname: c.fullname,
                                        email: c.email,
                                        mobile: c.mobile,
                                        enabledSMS: c.enabledSMS,
                                        enabledWhatsApp: c.enabledWhatsApp,
                                        enabledEmail: c.enabledEmail,
                                        isUser: users.map(u => u.email).includes(c.email) ? true : false

                                    }
                                    contactsTemp.push(item)
                                }
                            })

                            setContactsForCompany(contactsTemp)
                        })
                        .catch(err => console.error(err))
                })
                .catch(err => {
                    console.error(err)
                })

        }
        
    }, [companyId, recipientsModal])

    const handleSegmentsUpdate = (type: string, value: string | number | string[] | Moment | null, alm: AlarmData):void => {
        
        const segTemp = { ...segments }

        if (!tempAlarm) {
            return
        } else {
            /// Page 1
            if (type === 'type' || type === 'channel') {
                if (value) {

                    const noChannelTypes = ['signal', 'battery', 'temperature', 'noData']
                    if (!tempAlarm.type && type === 'type') {
                        ///1st type

                        if (value && noChannelTypes.includes(value)) {
                            segTemp.page1.num += 2
                        } else {
                            segTemp.page1.num += 1
                        }
                        segTemp.page1.text = `${textForScreen[value]}`


                    } else if (tempAlarm.type && !tempAlarm.channel && type === 'channel') {
                        //1st channel
                        segTemp.page1.num += 1
                        segTemp.page1.text = `${textForScreen[tempAlarm.type]} ${value}`
                    } else if (tempAlarm.type && type === 'type') {
                        alm.channel = ''
                        alm.condition = ''
                        alm.value = 0
                        alm.unit = ''
                        alm.clearCondition = ''
                        alm.clearValue = 0
                        alm.consumptionTimeframe = 0
                        alm.consumptionTimeframeUnit = ''
                        if (value && noChannelTypes.includes(value)) {
                            segTemp.page1.num = 2
                        } else {
                            segTemp.page1.num = 1
                        }

                        segTemp.page1.text = `${textForScreen[value]}`
                        segTemp.page2.text = ['', '']
                        segTemp.page2.num = 0

                    }

                    else if (tempAlarm.type && tempAlarm.channel && type == 'channel') {
                        segTemp.page1.text = `${textForScreen[tempAlarm.type]} ${value}`
                        segTemp.page1.num = 2
                    }
                }
                
            }

            ///Page 2

            value = value ? value.toString() : ''
            if (value) {

                if (tempAlarm?.type !== 'digital') {
                    ///condition
                    if (!tempAlarm.condition && type === 'condition') {
                        segTemp.page2.num += 1
                        if (tempAlarm?.type !== 'analog' && tempAlarm?.type !== 'analogDropOut') {
                            segTemp.page2.text[0] = value + ' ' + 0
                        } else {
                            segTemp.page2.text[0] = value + ' ' + 0 + ' ' + tempAlarm?.unit
                        }
                        setTimeout(() => {
                            const segUpdate = { ...segments }
                            segUpdate.page2.num += 1
                            setSegments(segUpdate)
                        }, 700)
                    } else if (tempAlarm.condition && type === 'condition') {
                        if (tempAlarm.type !== 'consumption') {
                            segTemp.page2.num = 2
                            segTemp.page2.text[0] = value + ' ' + 0
                            alm.value = 0
                            alm.unit = ''
                        } else {
                            if (editAlarm) {
                                segTemp.page2.num = 5
                                segTemp.page2.text[0] = `${value} ${tempAlarm.value} ${tempAlarm.unit}`
                            }
                        }
                    }

                    ///value
                    if (type === 'value' && !tempAlarm.value) {
                        segTemp.page2.text[0] = `${tempAlarm.condition} ${value} ${tempAlarm.unit}`
                    } else if (type === 'value' && tempAlarm.value) {
                        segTemp.page2.text[0] = `${tempAlarm.condition} ${value} ${tempAlarm.unit}`
                    }


                    ///unit 
                    if (type === 'unit' && !tempAlarm.unit) {
                        segTemp.page2.num += 1
                        segTemp.page2.text[0] = `${segTemp.page2.text[0]} ${value}`
                    } else if (type === 'unit' && tempAlarm.unit) {
                        segTemp.page2.text[0] = `${tempAlarm.condition} ${tempAlarm.value} ${value}`
                        if (tempAlarm.clearCondition) {
                            segTemp.page2.text[1] = `${tempAlarm.clearCondition} ${tempAlarm.clearValue} ${value}`
                        }
                    }

                    if (tempAlarm.type !== 'consumption') {
                        ///clearCondition
                        if (type === 'clearCondition' && !tempAlarm.clearCondition) {
                            segTemp.page2.num += 1
                            segTemp.page2.text[1] = `${value} 0 ${tempAlarm.unit}`
                            setTimeout(() => {
                                const segUpdate = { ...segments }
                                segUpdate.page2.num += 1
                                setSegments(segUpdate)
                            }, 700)
                        } else if (tempAlarm.clearCondition && type === 'clearCondition') {
                            if (!editAlarm) {
                                segTemp.page2.num = 5
                            }
                            segTemp.page2.text[1] = `${value} 0 ${tempAlarm.unit}`
                            alm.clearValue = 0
                        }


                        ///clear Value

                        if (type === 'clearValue') {
                            segTemp.page2.text[1] = `${tempAlarm.clearCondition} ${value} ${tempAlarm.unit}`
                        }
                    } else {
                        ///is consumption

                        if (type === 'consumptionTimeframe' && !tempAlarm.consumptionTimeframe) {

                            ///consumption timeframe
                            segTemp.page2.num += 1
                            segTemp.page2.text[1] = `${intl.formatMessage({ id: 'Within the last' })} ${value}`
                        } else if (type === 'consumptionTimeframe' && tempAlarm.consumptionTimeframe) {
                            segTemp.page2.text[1] = `${intl.formatMessage({ id: 'Within the last' })} ${value} ${tempAlarm.consumptionTimeframeUnit}`
                        }

                        if (type === 'consumptionTimeframeUnit' && !tempAlarm.consumptionTimeframeUnit) {
                            segTemp.page2.num += 1
                            segTemp.page2.text[1] = `${intl.formatMessage({ id: 'Within the last' })} ${tempAlarm.consumptionTimeframe} ${value}`
                        } else if (type === 'consumptionTimeframeUnit' && tempAlarm.consumptionTimeframeUnit) {
                            segTemp.page2.text[1] = `${intl.formatMessage({ id: 'Within the last' })} ${tempAlarm.consumptionTimeframe} ${value}`
                        }




                    }
                } else {
                    ///digital
                    if (type === 'value') {
                        if (value === '1') {
                            segTemp.page2.text[0] = `= ${intl.formatMessage({ id: 'ON' })} (1)`
                            segTemp.page2.text[1] = `= ${intl.formatMessage({ id: 'OFF' })} (0)`
                        } else {
                            segTemp.page2.text[0] = `= ${intl.formatMessage({ id: 'OFF' }) } (0)`
                            segTemp.page2.text[1] = `= ${intl.formatMessage({ id: 'ON' })} (1)`
                        }
                    }

                    if (type === 'clearValue') {
                        if (value === '1') {
                            segTemp.page2.text[0] = `= ${intl.formatMessage({ id: 'OFF' })} (0)`
                            segTemp.page2.text[1] = `= ${intl.formatMessage({ id: 'ON' })} (1)`
                        } else {
                            segTemp.page2.text[0] = `= ${intl.formatMessage({ id: 'ON' })} (1)`
                            segTemp.page2.text[1] = `= ${intl.formatMessage({ id: 'OFF' })} (0)`
                        }
                    }
                }

            }

            ///page3
            if (!tempAlarm.count && type === 'count' && value !== '0') {
                segTemp.page3.num = 1
                segTemp.page3.text[0] = `${value}`
            } else if (tempAlarm.count && type === 'count') {
                segTemp.page3.text[0] = `${value}`
            }

            if (!tempAlarm.clearCount && type === 'clearCount' && value !== '0') {
                segTemp.page3.num = 2
                segTemp.page3.text[1] = `${value}`
            } else if (tempAlarm.clearCount && type === 'clearCount') {
                segTemp.page3.text[1] = `${value}`
            }



            ///page 4
            if (type === 'message') {
                segTemp.page4.num = 1
                segTemp.page4.text[0] = value
                segTemp.page4.text[1] = '1'
                segTemp.page4.text[2] = '00:00 - 23:59'
                segTemp.page4.text[3] = intl.formatMessage({ id: '1st Jan - 31st Dec' })
            }
            if (type === 'maxAlarms') {
                segTemp.page4.text[1] = `${value}`
            }
            if (type === 'sendTimeframeStart') {
                segTemp.page4.text[2] = `${moment(value).format('HH:mm')} - ${moment(tempAlarm?.sendTimeframeEnd).format('HH:mm')}`
            }
            if (type === 'sendTimeframeEnd') {
                segTemp.page4.text[2] = `${moment(tempAlarm.sendTimeframeStart).format('HH:mm')} - ${moment(value).format('HH:mm')}`
            }

            if (type === 'dateRange') {
                segTemp.page4.text[3] = `${moment(alm.sendDateRangeStart).format('DD MMM')} - ${moment(alm.sendDateRangeEnd).format('DD MMM') }`
            }


            setSegments(segTemp)
            setTempAlarm(alm)
        }
    }

    



    const renderSwitch = (data: ColumnCellTemplateData<AlarmDataDisplay>):React.ReactNode | null => {
        if (data.data) {
            return <Switch
                value={data.data.on}
                onValueChange={(): void => handleSwitchClick(data.data.id)}
            />
        } else return
    }


    const handleSwitchClick = (id: number): void => {
        setLoading(true)
        const switchAlarmTemp = alarmData.find(alm => alm.id === id)
        
        if (switchAlarmTemp) {
            switchAlarmTemp.on = !switchAlarmTemp?.on
            saveAlarm(switchAlarmTemp, true)
        }
    }





    const saveAlarm = (tempAlarm: AlarmData, edit: boolean):void => {
        setLoading(true)
        const setInputText = tempAlarm.channel ? `${tempAlarm.type} ${tempAlarm.channel}` : tempAlarm.type
        let setInput = 0
        Object.keys(ALARM_TYPE_BY_SET_INPUT).forEach(k => {

            if (ALARM_TYPE_BY_SET_INPUT[k] === setInputText) {
                setInput = parseInt(k)
            }
        })

        if (setInput === 0) {
            if (tempAlarm.channel === 'C') {
                setInput = 3

            } else if (tempAlarm.channel === 'D') {
                setInput = 7
            }
        }
        let setOperator = 0
        let clrOperator = 0
        Object.keys(ALARM_CONDITION_KEYS).forEach(k => {

            if (ALARM_CONDITION_KEYS[k] === tempAlarm.condition) {
                
                setOperator = parseInt(k)
            }
            if (ALARM_CONDITION_KEYS[k] === tempAlarm.clearCondition) {
                clrOperator = parseInt(k)
            }
        })

        if (tempAlarm.type === 'battery') {
            tempAlarm.value = tempAlarm.value * 1000
            tempAlarm.clearValue = tempAlarm.clearValue * 1000
        }
        
        const redefinedAlarm: APIAlarmData = {
            id: tempAlarm.id,
            alarmMask: edit ? tempAlarm.alarmMask : 1 << alarmData.length,
            alarmMessage: tempAlarm.message,
            alarmClass: tempAlarm.alarmClass,
            setInput: setInput,
            clrInput: setInput,
            setOperator: setOperator,
            clrOperator: clrOperator,
            setThreshold: parseFloat(tempAlarm.value),
            clrThreshold: parseFloat(tempAlarm.clearValue),
            setCount: tempAlarm.count,
            clrCount: tempAlarm.clearCount,
            maxAlarms: tempAlarm.maxAlarms,
            on: tempAlarm.on,
            unit: tempAlarm.unit,
            consumptionTimeframe: tempAlarm.consumptionTimeframe,
            consumptionUnit: tempAlarm.consumptionTimeframeUnit,
            sendTimeframeStartUTC: tempAlarm.sendTimeframeStart ? moment(adjustTimeToUtc(moment(tempAlarm.sendTimeframeStart, 'HH:mm'))).format('HH:mm:ss') : null,
            sendTimeframeEndUTC: tempAlarm.sendTimeframeEnd ? moment(adjustTimeToUtc(moment(tempAlarm.sendTimeframeEnd, 'HH:mm'))).set({ seconds: 59 }).format('HH:mm:ss') : null,
            sendDateRangeStart: tempAlarm.sendDateRangeStart ? moment(tempAlarm.sendDateRangeStart).format('DDMM') : null,
            sendDateRangeEnd: tempAlarm.sendDateRangeEnd ?moment(tempAlarm.sendDateRangeEnd).format('DDMM') : null,
            fkLCLoggerId: LCLoggerId - 500000
        }


        const { id, ...redefinedAlarmWithoutId } = redefinedAlarm;

        const finalAlarm = edit ? redefinedAlarm : redefinedAlarmWithoutId

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(finalAlarm)
        }

        interface AlarmPostReturn {
            alarmContactsCount: number
            alarmId: number
        }

        CallPostAPI(CreateUrl(`/api/aquaguard/LCAlarmConfiguration`), requestOptions)
            .then(response => {
                return response.json()
            })
            .then((data: AlarmPostReturn) => { 
                console.log('getting response')
                if (data.alarmContactsCount === 0) {
                    console.log(tempAlarm)
                    const alm = { ...tempAlarm }
                    alm.id = data.alarmId
                    setTempAlarm(alm)
                    toggleRecipientsModal()
                } else {
                    setTempAlarm(undefined)
                }
                console.log(data)
                setOpenCreateAlarmModal(false)
                createSuccessNotification()
                setLoading(false)
                reloadData()
                
                setSegments({
                    page1: { num: 0, text: '' },
                    page2: { num: 0, text: ['', ''] },
                    page3: { num: 0, text: ['', ''] },
                    page4: { num: 0, text: ['', '', '', '', ''] },
                })
                setCurrentPage(1)
                setEditAlarm(false)
            })
            .catch(err => {
                console.error(err)
                createErrorNotification()
                setLoading(false)
                setOpenCreateAlarmModal(false)
                reloadData()
                setTempAlarm(undefined)
                setSegments({
                    page1: { num: 0, text: '' },
                    page2: { num: 0, text: ['', ''] },
                    page3: { num: 0, text: ['', ''] },
                    page4: { num: 0, text: ['', '', '', '', ''] },
                })
                setCurrentPage(1)
                setEditAlarm(false)
            })

    }

    const renderContactsBtn = (data: ColumnCellTemplateData<AlarmDataDisplay>): React.ReactNode | null => {
        if (data.data) {
            return (
                <div
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={(): void => handleContactClick(data.data.id)}
                        //disabled={/*sessionStorage.getItem('companyId') === '0'*/}
                    ><FormattedMessage id="Set / Edit who receives this alarm" /></Button>
                </div>
            )
        } else return
    }

    const handleContactClick = (id: number): void => { 
        const alarmTemp = alarmData.find(a => a.id === id)
        toggleRecipientsModal()
        if (alarmTemp) {
            setTempAlarm(alarmTemp)
        }
    }

    const toggleRecipientsModal = (): void => {
        if (recipientsModal) {
            reloadData()
            setRedirectId(null)
        }
        setRecipientsModal(!recipientsModal)
    }

    const renderEditBtn = (data: ColumnCellTemplateData<AlarmDataDisplay>): React.ReactNode => {
        return (
            <div
                style={{ display: 'flex', justifyContent: 'center' }}
            >
                <Button
                    variant='contained'
                    color='secondary'
                    onClick={():void => handleEditClick(data.data.id)}
                ><FormattedMessage id="Edit" /></Button>
            </div>
        )
    }


    const handleEditClick = (id: number):void => {
        const selectedAlarm: AlarmData | undefined = alarmData.find(alm => alm.id === id)
        if (selectedAlarm) {
            setEditAlarm(true)
            if (selectedAlarm.sendDateRangeStart || selectedAlarm.sendDateRangeEnd) {
                selectedAlarm.sendDateRangeStart = moment(selectedAlarm.sendDateRangeStart, 'DDMM')
                selectedAlarm.sendDateRangeEnd = moment(selectedAlarm.sendDateRangeEnd, 'DDMM')
            }
            setTempAlarm(selectedAlarm)
            setOpenCreateAlarmModal(!openCreateAlarmModal)
            let startTime = '00:00:00'
            let endTime = '23:59:59'
            let startDate = intl.formatMessage({ id: '1st Jan' })
            let endDate = intl.formatMessage({ id: '31st Dec' })
            if (selectedAlarm.sendTimeframeStart || selectedAlarm.sendTimeframeEnd) {
                startTime = moment(selectedAlarm.sendTimeframeStart, 'HH:mm').format('HH:mm')
                endTime = moment(selectedAlarm.sendTimeframeEnd, 'HH:mm').format('HH:mm')
            }
            if (selectedAlarm.sendDateRangeStart || selectedAlarm.sendDateRangeEnd) {
                startDate = moment(selectedAlarm.sendDateRangeStart).format('DD MMM')
                endDate = moment(selectedAlarm.sendDateRangeEnd).format('DD MMM')
            }
            const segs = { ...segments }
            segs.page1 = {
                num: 2,
                text: `${textForScreen[selectedAlarm.type]} ${selectedAlarm.channel}`
            }
            segs.page2 = {
                num: 5,
                text: [
                    `${selectedAlarm.condition} ${selectedAlarm.value} ${selectedAlarm.unit}`,
                    selectedAlarm.type !== 'consumption' ?
                        `${selectedAlarm.clearCondition} ${selectedAlarm.clearValue} ${selectedAlarm.unit}`
                        : `${intl.formatMessage({ id: 'Within the last' })} ${selectedAlarm.consumptionTimeframe} ${selectedAlarm.consumptionTimeframeUnit}`
                ]
            }
            segs.page3 = {
                num: 2,
                text: [
                    `${selectedAlarm.count}`,
                    `${selectedAlarm.clearCount}`
                ]
            }
            segs.page4 = {
                num: 1,
                text: [
                    `${selectedAlarm.message}`,
                    `${selectedAlarm.maxAlarms}`,
                    `${startTime} - ${endTime}`,
                    `${startDate} - ${endDate}`
                ]
                        }
            setSegments(segs)
        }
    }



    const renderDeleteBtn = (data: ColumnCellTemplateData<AlarmDataDisplay>, intl: IntlShape): React.ReactNode => {
        if (data.data?.id) {
            return (
                <div
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Button
                        variant='contained'
                        color='error'
                        onClick={(): void => handleAlarmDelete(data.data.id, intl)}
                    ><FormattedMessage id="Delete" /></Button>
                </div>
            )
        } else {
            return <p><FormattedMessage id="Alarm Error" /></p>
        }
    }

    const handleAlarmDelete = (id: number, intl: IntlShape):void => {
        const confirm = window.confirm(intl.formatMessage({ id: 'Are you sure you want to delete this alarm?' }))
        if (confirm) {
            CallDeleteAPI(CreateUrl(`/api/aquaguard/LCLAlarmConfig/${id}`), {})
                .then(data => { 
                    createSuccessNotification()
                    reloadData()
                })
                .catch(err => {
                    console.error(err)
                    createErrorNotification()
                })
        }
    }
    const renderStatus = (data: ColumnCellTemplateData<AlarmDataDisplay>, intl: IntlShape): React.ReactNode => {
        if (data.data) {
            if (data.data?.status === intl.formatMessage({ id: 'Active' })) {
                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'red' }}>
                        <i className="dx-icon-warning" style={{marginTop: 4} }></i>
                        <p>{data.data?.status.toUpperCase()}</p>
                        <i className="dx-icon-warning" style={{ marginTop: 4 }}></i>
                    </div>
                )
            } else {
                return data.data?.status.slice(0,1).toUpperCase() + data.data?.status.slice(1)
            }
        } else {
            return
        }
        
    }

    const closeContactModal = ():void => {
        setShowContacts(false)
    }

    const toggleContactModal = (): void => {
        if (showContacts) {
            if (redirectId) {
                const alarm = alarmData.find(alm => alm.id === redirectId)
                setTempAlarm(alarm)
                toggleRecipientsModal()
            }
            setShowContacts(!showContacts)
        } else {
            setShowContacts(!showContacts)
        }
    }

    const toggleCreateModal = (): void => {
        if (!openCreateAlarmModal) {
            setTempAlarm(newAlarm)
            setSegments(newSegments)
        }
        setOpenCreateAlarmModal(!openCreateAlarmModal)
    }

    const handleGroupModalClick = ():void => {
        setRecipientsModal(false)
        setRecipientsGroupModal(true)
    }

    const handleAddContactsToAlarm = (email: string): void => {
        const alm = { ...tempAlarm }
        if (alm.contacts.includes(email)) {
            alm.contacts = alm.contacts.filter(a => a !== email)
        } else {
            alm.contacts = [...alm.contacts, email]
        }
        setTempAlarm(alm)
    }

    const handleAddContactsGroupToAlarm = (emailArr: string[]): void => {
        const alm = { ...tempAlarm }
        alm.contacts = [...alm.contacts, ...emailArr]
        setTempAlarm(alm)
        setRecipientsGroupModal(false)
        setRecipientsModal(true)
    }

    const handleReturnToRecipientsModal = ():void => {
        setRecipientsGroupModal(false)
        setRecipientsModal(true)
    }

    const handleSaveAlarm = (): void => {
        setLoading(true)
        if (tempAlarm) {
            if (editAlarm) {
                saveAlarm(tempAlarm, true)
            } else {
                saveAlarm(tempAlarm, false)
            }
        }
    }

    const toggleHelpModal = ():void => {
        setShowHelpModal(!showHelpModal)
        setOpenCreateAlarmModal(!openCreateAlarmModal)
    }

    const renderViewAlarmDetails = (data):React.ReactNode => {
        return (
            <div
                style={{display: 'flex', justifyContent: 'center', width: '100%'} }
            >
                <Button
                    variant="contained"
                    color="warning"
                    onClick={(): void => handleAlarmHistoryClick(data.data.id)}
                >
                    <FormattedMessage id="History" />
                </Button>
            </div>
        )
    }

    const renderLastTriggered = (data: ColumnCellTemplateData<AlarmDataDisplay>, intl: IntlShape): React.ReactNode => {
        if (data.data.lastTriggered !== intl.formatMessage({ id: 'No Alarm' })) {
            return <div style={{ color: data.data?.status === intl.formatMessage({ id: 'Active' }) ? 'red' : 'inherit' }}>{moment(data.data.lastTriggered).format('DD MMM YYYY HH:mm:ss')}</div>
        } else {
            return data.data.lastTriggered
        }
    }

    const handleAlarmHistoryClick = (id:number):void => {
        const selectedAlarm = alarmData.find(alm => alm.id === id)
        setTempAlarm(selectedAlarm)
        setShowHistoryModal(true)
    }


    const handleCustomTimeframe = (isCustom: boolean): void => {
         
        const alm = { ...tempAlarm }
        if (isCustom) {
            alm.sendTimeframeStart = moment('00:00', 'HH:mm')
            alm.sendTimeframeEnd = moment('23:59', 'HH:mm')
        } else {
            const segs = {...segments}
            alm.sendTimeframeStart = null
            alm.sendTimeframeEnd = null
            segs.page4.text[2] = '00:00 - 23:59'
            setSegments(segs)
        }
        setTempAlarm(alm)
    }

    const handleCustomDateRange = (isCustom:boolean):void => {
        const alm = { ...tempAlarm }
        if (isCustom) {
            alm.sendDateRangeStart = moment('2026-01-01', 'YYYY-MM-DD')
            alm.sendDateRangeEnd = moment('2026-12-31', 'YYYY-MM-DD')
        } else {
            const segs = { ...segments }
            alm.sendDateRangeStart = null
            alm.sendDateRangeEnd = null
            segs.page4.text[3] = intl.formatMessage({ id: `1st Jan - 31st Dec` })
            setSegments(segs)
        }
        setTempAlarm(alm)
    }

    const handleCloseHistory = ():void => {
        setShowHistoryModal(false)
        setTempAlarm(undefined)
    }

    const handleSwitchToAlarmContacts = (alarmId: number | null):void => {
        toggleRecipientsModal()
        toggleContactModal()
        setRedirectId(alarmId)
    }




    return (

        <>
            {loading &&
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 13000,
                    height: 300
                }}>
                    <Loader color={undefined} />
                </div>
            }
            {!loading &&
                <div>
                    <AlarmHistoryModal
                        show={showHistoryModal}
                        tempAlarm={tempAlarm}
                        handleClose={handleCloseHistory}
                    />
                    <AlarmHelpModal
                        show={showHelpModal}
                        tempAlarm={tempAlarm}
                        toggleHelpModal={toggleHelpModal}
                        currentPage={currentPage}
                    />
                    <AlarmSetModal
                        show={openCreateAlarmModal}
                        close={toggleCreateModal}
                        tempAlarm={tempAlarm}
                        model={model}
                        serial={serial}
                        handleAlarmChanges={handleAlarmChanges}
                        segments={segments}
                        editAlarm={editAlarm}
                        handleSaveAlarm={handleSaveAlarm}
                        setTempAlarm={setTempAlarm}
                        toggleHelpModal={toggleHelpModal}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handleCustomTimeframe={handleCustomTimeframe}
                        handleCustomDateRange={handleCustomDateRange }
                    />
                    <AlarmContactModal
                        showContacts={showContacts}
                        toggleContactModal={toggleContactModal}
                        tempAlarm={tempAlarm}
                        companyId={companyId}
                        closeContactModal={closeContactModal}
                     />
                    <RecipientsModal
                        show={recipientsModal}
                        handleGroupModalClick={handleGroupModalClick}
                        tempAlarm={tempAlarm}
                        contactsForCompany={contactsForCompany}
                        handleAddContactsToAlarm={handleAddContactsToAlarm}
                        close={toggleRecipientsModal}
                        handleSwitchToAlarmContacts={handleSwitchToAlarmContacts}
                        
                    />
                    {recipientsGroupModal && <AlarmRecipientsGroupModal
                        show={recipientsGroupModal}
                        contactsForCompany={contactsForCompany}
                        handleAddContactsGroupToAlarm={handleAddContactsGroupToAlarm}
                        handleBack={handleReturnToRecipientsModal}
                        companyId={companyId}
                    />}
                    <div style={{display: 'flex', width: '100%'} }>
                        <h4 style={{ margin: '5px 20px 0 0' }}><b><FormattedMessage id='Current Alarms' /></b></h4>
                        {currentAlarms && currentAlarms.length >= 16 ? <Tooltip title={intl.formatMessage({ id: 'Maximum of 16 alarms per device' })}>
                            <Button variant="contained" color='primary' disabled={true} style={{ marginRight: 10 }}><FormattedMessage id="Create New Alarm" /></Button>
                        </Tooltip> :
                        <Button variant="contained" color='primary' disabled={currentAlarms && currentAlarms?.length >= 16} style={{ marginRight: 10 }} onClick={(): void => toggleCreateModal()}><FormattedMessage id="Create New Alarm" /></Button>}
                        <Button variant="contained" color='secondary' onClick={(): void => toggleContactModal()}><FormattedMessage id="Set / Edit How Recipients will receive alarms" /></Button>
                    </div>
                    <DataGrid
                        dataSource={currentAlarms}
                        allowColumnReordering={true}
                    >
                        <StateStoring enabled={true} type="localStorage" storageKey="alarmListGrid" />
                        <Export enabled={true} />
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} search={{ enabled: true }} />
                        <Column dataField='on' cellRender={(data): React.ReactNode => renderSwitch(data)} caption={intl.formatMessage({ id: 'Enabled / Disabled' })} width={80} />
                        <Column dataField='created' dataType='date' caption={intl.formatMessage({id: 'Created'})} />
                        <Column dataField='createdBy' dataType='string' caption={intl.formatMessage({ id: 'Created by' })} />
                        <Column dataField='lastTriggered' dataType='string' cellRender={(data): React.ReactNode => renderLastTriggered(data, intl)} caption={intl.formatMessage({id: 'Last Triggered'})} />
                        <Column dataField='status' dataType='string' cellRender={(data): React.ReactNode => renderStatus(data, intl)} caption={intl.formatMessage({id: 'Status'}) } />
                        <Column dataField='type' dataType='string' width={250} caption={intl.formatMessage({id: 'Type'})} />
                        <Column dataField='condition' dataType='string' caption={intl.formatMessage({id: 'Trigger Condition'})} />
                        <Column dataField='clearCondition' dataType='string' caption={intl.formatMessage({ id: 'Clear Condition' })} />
                        <Column dataField='message' dataType='string' caption={intl.formatMessage({id: 'Message / Name'})} />
                        <Column dataField='contacts' cellRender={(data): React.ReactNode => renderContactsBtn(data)} width={sessionStorage.getItem('language') !== 'en-GB' ? 350 : 300} caption={intl.formatMessage({id: 'Contacts'})} />
                        <Column cellRender={(data): React.ReactNode => renderEditBtn(data)} caption={intl.formatMessage({ id: "Edit" })} width={sessionStorage.getItem('language') !== 'en-GB' ? 100 : 80} />
                        <Column cellRender={(data): React.ReactNode => renderDeleteBtn(data, intl)} caption={intl.formatMessage({ id: "Delete" })} width={100} />
                        <Column cellRender={(data): React.ReactNode => renderViewAlarmDetails(data)} caption={intl.formatMessage({ id: 'Alarm Trigger History' })} width={100} />
                    </DataGrid>
                </div>
            }
        </>
    )
}
export default Alarms